import React, { useState, useEffect } from "react";
import { LoadingSpinner } from "@arctheme-components/elements/LoadingSpinner/LoadingSpinner";
import { MinicartHeader } from "./MinicartBlocks/MinicartHeader";
import { MinicartUSP } from "./MinicartBlocks/MinicartUSP";
import { MinicartItems } from "./MinicartBlocks/MinicartItems";
import { MinicartDiscounts } from "./MinicartBlocks/MinicartDiscounts";
import { MinicartUpgrade } from "./MinicartBlocks/MinicartUpgrade";
import { MinicartTotals } from "./MinicartBlocks/MinicartTotals";
import { MinicartNote } from "./MinicartBlocks/MinicartNote";
import { MinicartActions } from "./MinicartBlocks/MinicartActions";
import { MinicartEmptyState } from "./MinicartBlocks/MinicartEmptyState";
import useCartData from "@arctheme-hooks/useCartData";
import useHeaderMenuStore from '@src/stores/HeaderMenuStore';
import { useHeaderHeight } from '@src/hooks/useHeaderHeight';
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "../../../../entrypoints/theme";

export const Minicart = ({ cartData, style = "drawer", props }) => {
  const { cart, fetchCart }: any = useCartData();
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const { currentMenu } = useHeaderMenuStore();
  const openMenu = useHeaderMenuStore((state) => state.openMenu);
  const closeMenu = useHeaderMenuStore((state) => state.closeMenu);

  const [cartItemCount, setCartItemCount] = useState(0);

  

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const response = await fetch(`/cart.js`);
        const cartData = await response.json();
        const updatedItemCount = cartData.item_count || 0;
        setCartItemCount(updatedItemCount);
        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(element => {
          const count = parseInt(updatedItemCount);
          const text = count === 1 ? 'item' : 'items';
          element.textContent = `${count} ${text}`;
        });
      } catch (error) {
        console.error("Error fetching cart:", error);
      }
    };

    // Listen for cart updates
    document.addEventListener("liquid-ajax-cart:request-end", function(event: any) {
      fetchCart();
    });

    // Initial cart fetch
    fetchCart();
    
    // Clean up
    return () => {
      document.removeEventListener('liquid-ajax-cart:request-end', () => {});
    };
  }, []);


  // Extract global minicart settings with defaults
  const {
    background_color = "#ffffff",
    border_color = "#e5e7eb",
    border_width = "1px",
    border_style = "solid",
    border_radius = "32px",
    box_shadow = "0 30px 130px -8px rgba(0, 0, 0, 0.25)",
    block_gap = 16,
    padding_top_desktop = 24,
    padding_bottom_desktop = 24,
    padding_left_right_desktop = 24,
    padding_top_mobile = 14,
    padding_bottom_mobile = 14,
    padding_left_right_mobile = 14,
  } = props.settings || {};

  // Generate block objects for easier access to settings
  const blocks = {};
  props.blocks?.forEach(block => {
    blocks[block.type] = {
      settings: block.settings,
      id: block.id
    };
  });

  const openMiniCart = (event) => {
    const { requestState } = event.detail;
    if (requestState.requestType === "add" && requestState.responseData?.ok) {
      openMenu("CART", isMobile);
    }
  };

  useEffect(() => {
    document.addEventListener("liquid-ajax-cart:request-end", openMiniCart);
    document.addEventListener("toggle-minicart", (event) => {
      const { isMinicartVisible } = event.detail;
      isMinicartVisible ? openMenu("CART", isMobile) : closeMenu(isMobile);
    });

    return () => {
      document.removeEventListener("liquid-ajax-cart:request-end", openMiniCart);
      document.removeEventListener("toggle-minicart", () => {});
    };
  }, []);

  const [isUpsellOpen, setIsUpsellOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleUpsellToggle = (isOpen) => {
    setIsUpsellOpen(isOpen);
  };


  // Dynamically calculate padding based on device
  const paddingStyle = isMobile
    ? `${padding_top_mobile}px ${padding_left_right_mobile}px ${padding_bottom_mobile}px`
    : `${padding_top_desktop}px ${padding_left_right_desktop}px ${padding_bottom_desktop}px`;

  // Dynamically create border from separate settings
  const borderStyle = `${border_width} ${border_style} ${border_color}`;

  return (
    <>
      <div
        className={`minicart-overlay fixed inset-0 lg:hidden bg-black/50 z-[999] ${
          currentMenu === "CART" ? "block" : "hidden"
        }`}
        onClick={() => closeMenu(isMobile)}
      ></div>
      <div
        className={`minicart-wrapper  ${style} relative ${
          currentMenu === "CART" ? "" : "hidden"
        } z-[1000] max-h-[98vh]`}
        style={{
          height: isMobile ? null : null,
          top: isMobile ? null : null,
        }}
      >
        <div 
          className="minicart gap-2 lg:gap-4"
          style={{
            backgroundColor: background_color,
            border: borderStyle,
            borderRadius: border_radius,
            boxShadow: box_shadow,
            padding: paddingStyle,
            height: style === "drawer" && isMobile ? "100dvh" : "auto",
            maxHeight: "calc(100dvh - 55px)",
            width: style === "drawer" ? (isMobile ? "100%" : "593px") : "auto",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Close Button */}
          
          <LoadingSpinner id={"cart-spinner"} />
          
          {/* Render blocks */}
          {blocks.header && <MinicartHeader settings={blocks.header.settings} item_count={cartItemCount}  onClose={() => closeMenu(isMobile)} />}
          
          {blocks.usp && <MinicartUSP settings={blocks.usp.settings} />}
          
          <div className="flex-1 overflow-y-auto lg:max-h-[400px]">
            {blocks.items && cart?.items?.length > 0 ? (
              <MinicartItems settings={blocks.items.settings} items={cart.items} global_settings={props} />
            ) : (
              blocks.empty_state && (
                <MinicartEmptyState
                  settings={blocks.empty_state.settings}
                  onClose={() => closeMenu(isMobile)}
                />
              )
            )}
          </div>
          
          {cart?.items?.length > 0 && (
            <div className="minicart__bottom upgrade-minicart mt-auto ">
              {blocks.upgrade && (
                <MinicartUpgrade 
                  items={cart?.items} 
                  settings={blocks.upgrade.settings} 
                  onAccordionToggle={handleUpsellToggle} 
                />
              )}
              
              {blocks.totals && (
                <MinicartTotals
                  cart_subtotal={cart?.items_subtotal_price / 100}
                  cart_items={cart?.items}
                  settings={blocks.totals.settings}
                />
              )}
              
              {blocks.free_shipping && (
                <MinicartDiscounts
                  cart_subtotal={cart?.items_subtotal_price / 100}
                  settings={blocks.free_shipping.settings}
                />
              )}
              
              {blocks.note && blocks.note.settings?.show_cart_note && (
                <MinicartNote settings={blocks.note.settings} />
              )}
              
              {blocks.actions && (
                <MinicartActions
                  show_cart={blocks.actions.settings?.show_view_cart}
                  settings={blocks.actions.settings}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
